<template>
  <yv-form-search
    :formSearch="formSearch"
    :enableExport="false"
    @search="search"
    ref="formSearch"
  >
    <template #search-actions="{ actions }">
      <div class="text-center">
        <md-button
          native-type="submit"
          @click.native.prevent="actions.search"
          class="md-info md-round"
          >{{ $t("common.report") }}
        </md-button>
      </div>
    </template>
  </yv-form-search>
</template>

<script>
import YvFormSearch from "@/components/Base/YvFormSearch.vue";
import { service_paths } from "@/constants/Services";
import { getId, initLoader, Message } from "@/helpers/utils";
import Role from "@/constants/Role";
import { mapGetters, mapState } from "vuex";
import { store } from "@/store";
import { hasValidRole } from "@/helpers/role-helper";

const FilteringFields = {
  studentId: "student_id",
  programId: "program_id",
  termId: "term_id",
  classIds: "class_ids"
};

export default {
  components: { YvFormSearch },
  data() {
    const formSearch = this.generateFormSearchByRole(
      store.getters["user/role"]
    );

    return {
      formSearch,
      hasFirstSearch: false
    };
  },
  inject: ["reportParams"],
  created() {
    this.initialize();
  },
  computed: {
    ...mapGetters("user", {
      userRole: "role",
      userInfo: "info"
    }),
    ...mapState("switchAccountChild", ["currentChild"]),
    filteringFieldIdxs() {
      return this.formSearch.reduce((fieldIdxs, fieldObj, idx) => {
        fieldIdxs[fieldObj.name] = idx;
        return fieldIdxs;
      }, {});
    },
    selectedStudentId() {
      if (hasValidRole(this.userRole, Role.Student)) {
        return getId(this.userInfo);
      }

      if (hasValidRole(this.userRole, Role.Family)) {
        return getId(this.currentChild);
      }

      return this.formSearch[this.filteringFieldIdxs[FilteringFields.studentId]]
        .value;
    },
    selectedProgramId: {
      get() {
        return this.formSearch[
          this.filteringFieldIdxs[FilteringFields.programId]
        ].value;
      },
      set(value) {
        this.formSearch[
          this.filteringFieldIdxs[FilteringFields.programId]
        ].value = value;
      }
    },
    selectedTermId: {
      get() {
        return this.formSearch[this.filteringFieldIdxs[FilteringFields.termId]]
          .value;
      },
      set(value) {
        this.formSearch[
          this.filteringFieldIdxs[FilteringFields.termId]
        ].value = value;
      }
    },
    selectedClassIds: {
      get() {
        return this.formSearch[
          this.filteringFieldIdxs[FilteringFields.classIds]
        ].value;
      },
      set(value) {
        this.formSearch[
          this.filteringFieldIdxs[FilteringFields.classIds]
        ].value = value;
      }
    },
    isStudentView() {
      return hasValidRole(this.userRole, [Role.Student, Role.Family]);
    },
    isDefaultView() {
      return (
        hasValidRole(this.userRole, [Role.Admin, Role.Principal]) ||
        !hasValidRole(this.userRole, Object.values(Role))
      );
    }
  },
  methods: {
    generateFormSearchByRole(role) {
      const baseFormSearch = [
        {
          label: this.$t("attendance_report.student.label_filter_by_program"),
          name: "program_id",
          value: "",
          type: "select",
          options: []
        },
        {
          label: this.$t("common.term"),
          name: "term_id",
          value: "",
          type: "select",
          options: []
        },
        {
          label: this.$t("common.class"),
          name: "class_ids",
          value: [],
          type: "checkbox-multi-select",
          options: [],
          trackBy: "id",
          displayField: "name",
          showSelectAll: true,
          width: 30
        }
      ];

      if (hasValidRole(role, [Role.Student, Role.Family])) {
        return baseFormSearch;
      }

      if (hasValidRole(role, Role.Teacher)) {
        return [
          {
            label: "Student",
            name: "student_id",
            value: "",
            options: [],
            displayField: "displayName",
            type: "select"
          },
          ...baseFormSearch
        ];
      }

      return [
        {
          label: this.$t("attendance_report.student.label_filter_by_student"),
          name: "student_id",
          value: "",
          displayValue: "",
          options: [],
          type: "autocomplete",
          fields: {
            text: "displayName",
            value: "id"
          },
          searchKey: "searchKey",
          refetchOptions: this.fetchStudentOpts,
          width: 30
        },
        ...baseFormSearch
      ];
    },
    search(queryParams) {
      if (!this.selectedStudentId) {
        Message.error("Please choose a student");
        return;
      }

      this.$emit("set-new-report-params", {
        ...queryParams,
        class_ids: this.reFormatSelectedClassIds(queryParams.class_ids)
      });
    },
    fetchStudentOpts(queryObj) {
      const path = this.getStudentPath();
      queryObj = this.reGenQueryObjByUserRole(queryObj);

      return this.$axios
        .get(path, queryObj, undefined, undefined, undefined, undefined, false)
        .then(response => {
          this.formSearch[
            this.filteringFieldIdxs[FilteringFields.studentId]
          ].options = response.data.data.map(student => ({
            ...student,
            id: getId(student),
            displayName: this.generateStudentDisplayName(student)
          }));
        });
    },
    fetchProgramOptsBy(studentId) {
      return this.$axios
        .get(service_paths.programs, { limit: 200, user_id: studentId })
        .then(response => {
          const programs = response.data.data.map(program => ({
            ...program,
            id: getId(program)
          }));

          this.formSearch[
            this.filteringFieldIdxs[FilteringFields.programId]
          ].options = programs;
          this.selectedProgramId = programs[0]?.id;

          return this.selectedProgramId;
        });
    },
    fetchTermsBy(programId) {
      return this.$axios
        .get(service_paths.terms, {
          limit: 200,
          program_id: programId,
          order_by: "created_at ASC"
        })
        .then(response => {
          const terms = response.data.data.map(term => ({
            ...term,
            id: getId(term)
          }));

          this.formSearch[
            this.filteringFieldIdxs[FilteringFields.termId]
          ].options = terms;
          this.selectedTermId = terms[0]?.id;

          return this.selectedTermId;
        });
    },
    fetchClassesBy(termId) {
      return this.$axios
        .get(service_paths.classes, {
          limit: 200,
          term_id: termId,
          // FIX-ME: using constant to config & get status value
          status_in: ["On-going", "Concluded"]
        })
        .then(response => {
          const classes = response.data.data.map(classObj => ({
            ...classObj,
            id: getId(classObj)
          }));

          this.formSearch[
            this.filteringFieldIdxs[FilteringFields.classIds]
          ].options = classes;
          this.selectedClassIds = classes.map(classObj => getId(classObj));

          return this.selectedClassIds;
        });
    },
    getStudentPath() {
      const userId = getId(this.userInfo);

      if (this.isDefaultView) {
        return `${service_paths.students}/filter`;
      }

      return `${service_paths.users}/${userId}/assigned-students`;
    },
    reGenQueryObjByUserRole(queryParams) {
      if (!queryParams) return null;

      if (this.isDefaultView) {
        const { searchKey, restParams } = queryParams;

        return {
          ...restParams,
          search_key: searchKey,
          fields: ["full_name", "student_code"]
        };
      }

      if (hasValidRole(this.userRole, Role.Teacher)) {
        return {
          limit: 9999,
          teacher_id: getId(this.userInfo)
        };
      }

      return null;
    },
    setUpDefaultParams() {
      this.fetchProgramOptsBy(this.selectedStudentId);
    },
    initialize() {
      if (this.isStudentView) {
        this.setUpDefaultParams();
        return;
      }

      const loader = initLoader();
      this.fetchStudentOpts().finally(() => loader.hide());
    },
    reFormatSelectedClassIds(selectedClassIds) {
      const filteringClassOpt = this.formSearch[
        this.filteringFieldIdxs[FilteringFields.classIds]
      ];
      // ? in selecting-all case
      if (selectedClassIds.length === filteringClassOpt.options.length) {
        return [];
      }
      return selectedClassIds;
    },
    generateStudentDisplayName(student) {
      if (!!student.student_code) {
        return `${student.full_name} - ${student.student_code}`;
      }

      return student.full_name;
    },
    resetFilteringOpt(idx, defaultValue) {
      this.formSearch[idx].options = [];
      this.formSearch[idx].value = defaultValue;
    }
  },
  watch: {
    selectedStudentId: function(newSelectedStudentId) {
      if (!newSelectedStudentId) {
        this.resetFilteringOpt(
          this.filteringFieldIdxs[FilteringFields.programId],
          ""
        );
        return;
      }
      this.fetchProgramOptsBy(newSelectedStudentId);
    },
    selectedProgramId: function(newSelectedProgramId) {
      if (!newSelectedProgramId) {
        this.resetFilteringOpt(
          this.filteringFieldIdxs[FilteringFields.termId],
          ""
        );
        return;
      }
      this.fetchTermsBy(newSelectedProgramId);
    },
    selectedTermId: function(newSelectedTermId) {
      if (!newSelectedTermId) {
        this.resetFilteringOpt(
          this.filteringFieldIdxs[FilteringFields.classIds],
          ""
        );
        return;
      }
      this.fetchClassesBy(newSelectedTermId);
    },
    selectedClassIds: {
      deep: true,
      handler: function(newSelectedClassIds) {
        if (this.isStudentView && !this.hasFirstSearch) {
          this.hasFirstSearch = true;
          this.search({
            student_id: this.selectedStudentId,
            program_id: this.selectedProgramId,
            term_id: this.selectedTermId,
            class_ids: this.reFormatSelectedClassIds(newSelectedClassIds)
          });
        }
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
